import { useRef, useState } from 'react';
import './PayStubModal.css';
import { useTranslation } from 'react-i18next';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { Button, Typography, Row, Col, Card, Space, Avatar, Divider, Statistic } from 'antd';
import { 
  DownloadOutlined, 
  UserOutlined, 
  BankOutlined, 
  PhoneOutlined, 
  IdcardOutlined, 
  DollarOutlined,
  ClockCircleOutlined,
  BookOutlined,
  CalculatorOutlined,
  WalletOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;

const formatNumber = (number, locale?) => {
  if (isNaN(number)) {
    return '0.00';
  }
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(number);
};

const formatDate = (dateString, locale?) => {
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, '0');
  const month = date.toLocaleString(locale, { month: 'short' });
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

const PayStubModal = ({ payStubData, onClose }: { payStubData: any; onClose?: () => void }) => {
  const { t, i18n } = useTranslation();
  const componentRef = useRef();
  const [isButtonHidden, setIsButtonHidden] = useState(false);
  const [logoError, setLogoError] = useState(false);

  if (!payStubData) {
    return null;
  }

  const formatPayPeriod = (periodString) => {
    if (periodString && periodString.includes('_')) {
      const [startDateStr, endDateStr] = periodString.split('_');
      
      const startParts = startDateStr.split('-').map(Number);
      const endParts = endDateStr.split('-').map(Number);
      
      const startDate = new Date(startParts[0], startParts[1] - 1, startParts[2]);
      const endDate = new Date(endParts[0], endParts[1] - 1, endParts[2]);
      
      const startFormatted = startDate.getDate() + ' ' + 
                            startDate.toLocaleString(i18n.language, { month: 'short' });
      
      const endFormatted = endDate.getDate() + ' ' + 
                          endDate.toLocaleString(i18n.language, { month: 'short' });
      
      return `${startFormatted} - ${endFormatted}`;
    }
    
    return periodString;
  };

  const handleDownloadPDF = () => {
    setIsButtonHidden(true);
    const element = componentRef.current;

    html2canvas(element).then((canvas) => {
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      pdf.addImage(imgData, 'PNG', 10, 10, 190, 0);
      pdf.save('paystub.pdf');
      setIsButtonHidden(false);
    });
  };

  // Helper function to parse hours string like "2h 30m" to decimal
  const parseHoursToDecimal = (hoursString) => {
    if (typeof hoursString === 'number') return hoursString;
    if (!hoursString) return 0;
    
    // Handle "Xh Ym" format
    if (hoursString.includes('h') || hoursString.includes('m')) {
      const hoursMatch = hoursString.match(/(\d+)h/);
      const minutesMatch = hoursString.match(/(\d+)m/);
      
      const hours = hoursMatch ? parseInt(hoursMatch[1]) : 0;
      const minutes = minutesMatch ? parseInt(minutesMatch[1]) : 0;
      
      return hours + (minutes / 60);
    }
    
    // Handle "HH:MM" format
    if (hoursString.includes(':')) {
      const [hours, minutes] = hoursString.split(':').map(Number);
      return hours + (minutes / 60);
    }
    
    // If it's already a number string
    return parseFloat(hoursString) || 0;
  };

  // Format decimal hours back to "Xh Ym" format
  const formatHoursDisplay = (decimalHours) => {
    const hours = Math.floor(decimalHours);
    const minutes = Math.round((decimalHours - hours) * 60);
    
    if (minutes === 0) {
      return `${hours}h`;
    }
    return `${hours}h ${minutes}m`;
  };

  // Calculate summary data with fixed hours calculation
 // In PayStubModal.jsx, update the calculateSummary function:

const calculateSummary = () => {
  if (!payStubData.classes || payStubData.classes.length === 0) {
    return {
      totalClasses: 0,
      totalHours: 0,
      totalHoursDisplay: '0h',
      averageRate: 0,
      subtotal: 0,
      withholdingTax: 0,
      netPay: 0
    };
  }

  const totalClasses = payStubData.classes.length;
  
  const totalHours = payStubData.classes.reduce((acc, cls) => {
    const hours = parseHoursToDecimal(cls.hours);
    return acc + hours;
  }, 0);
  
  const subtotal = payStubData.classes.reduce((acc, cls) => {
    const hours = parseHoursToDecimal(cls.hours);
    return acc + (cls.rate * hours);
  }, 0);
  
  const averageRate = totalHours > 0 ? subtotal / totalHours : 0;
  
  // ✅ Use the withholding tax from payStubData instead of calculating it
  const withholdingTax = payStubData.withholdingTax || 0;
  const netPay = subtotal - withholdingTax;

  return {
    totalClasses,
    totalHours,
    totalHoursDisplay: formatHoursDisplay(totalHours),
    averageRate,
    subtotal,
    withholdingTax,
    netPay
  };
};

  const summary = calculateSummary();

  return (
    <div className="paystub-content" ref={componentRef}>
      {/* Header Card */}
      <Card 
        className="paystub-header-card" 
        bordered={false}
        style={{ marginBottom: 24, backgroundColor: '#fafafa' }}
      >
        <Row gutter={[24, 16]} align="middle">
          <Col xs={24} sm={12} md={8} lg={6}>
            {payStubData.companyLogo && !logoError ? (
              <img
                src={payStubData.companyLogo}
                alt="Company Logo"
                className="company-logo"
                style={{ maxWidth: '120px', height: 'auto' }}
                onError={() => setLogoError(true)}
              />
            ) : (
              <Avatar shape="square" size={100} icon={<BankOutlined />} style={{ backgroundColor: '#f0f0f0', color: '#8c8c8c' }} />
            )}
          </Col>
          
          <Col span={10}>
            <div style={{ fontSize: '13px', lineHeight: 1.3 }}>
              <Title level={5} style={{ margin: '0 0 4px 0', fontSize: '14px', color: '#262626' }}>
                {payStubData.companyName || 'Company Name'}
              </Title>
              <Text type="secondary" style={{ display: 'block' }}>{payStubData.companyAddress || 'Company Address'}</Text>
              <Text type="secondary" style={{ display: 'block' }}>{payStubData.companyWebsite || 'Company Website'}</Text>
              <Text type="secondary" style={{ display: 'block' }}>{payStubData.companyTax || 'Company Tax'}</Text>
              <Text type="secondary" style={{ display: 'block' }}>{payStubData.companyPhone || 'Company Phone'}</Text>
            </div>
          </Col>
          
          <Col span={6}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ marginBottom: 8 }}>
                <Text type="secondary" style={{ fontSize: '12px' }}>{t('Period')}</Text>
                <br />
                <Text strong style={{ fontSize: '14px' }}>
                  {payStubData.payPeriod && payStubData.payPeriod.includes('_') 
                    ? formatPayPeriod(payStubData.payPeriod)
                    : payStubData.payPeriod || 'N/A'}
                </Text>
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: '12px' }}>{t('Pay Date')}</Text>
                <br />
                <Text strong style={{ fontSize: '14px' }}>
                  {payStubData.payDate ? formatDate(payStubData.payDate) : 'N/A'}
                </Text>
              </div>
            </div>
          </Col>
        </Row>
      </Card>

      {/* Employee Info */}
      <Card 
        title={<Space><UserOutlined style={{ color: '#595959' }} /> <Text style={{ color: '#595959' }}>{t('Employee Information')}</Text></Space>}
        style={{ marginBottom: 24 }}
        size="small"
        bordered={false}
      >
        <Row gutter={[16, 12]}>
          <Col xs={24} sm={12}>
            <Text type="secondary">{t('Name')}</Text>
            <br />
            <Text strong>{payStubData.teacherName || 'N/A'}</Text>
          </Col>
          <Col xs={24} sm={12}>
            <Text type="secondary">{t('National ID')}</Text>
            <br />
            <Text strong>{payStubData.teacherNationalId || 'N/A'}</Text>
          </Col>
          <Col xs={24} sm={12}>
            <Text type="secondary">{t('Address')}</Text>
            <br />
            <Text strong>{payStubData.teacherAddress || 'N/A'}</Text>
          </Col>
          <Col xs={24} sm={12}>
            <Text type="secondary">{t('Phone')}</Text>
            <br />
            <Text strong>{payStubData.teacherPhone || 'N/A'}</Text>
          </Col>
        </Row>
      </Card>

      {/* Summary Statistics - More Subtle */}
      <Row gutter={[12, 12]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card size="small" bordered={false} style={{ backgroundColor: '#fafafa' }}>
            <Statistic
              title={<Text type="secondary" style={{ fontSize: '12px' }}>{t('Classes')}</Text>}
              value={summary.totalClasses}
              valueStyle={{ fontSize: '20px', color: '#262626' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" bordered={false} style={{ backgroundColor: '#fafafa' }}>
            <Statistic
              title={<Text type="secondary" style={{ fontSize: '12px' }}>{t('Total Hours')}</Text>}
              value={summary.totalHoursDisplay}
              valueStyle={{ fontSize: '20px', color: '#262626' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" bordered={false} style={{ backgroundColor: '#fafafa' }}>
            <Statistic
              title={<Text type="secondary" style={{ fontSize: '12px' }}>{t('Avg Rate/Hr')}</Text>}
              value={formatNumber(summary.averageRate)}
              valueStyle={{ fontSize: '20px', color: '#262626' }}
              prefix="฿"
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" bordered={false} style={{ backgroundColor: '#fafafa' }}>
            <Statistic
              title={<Text type="secondary" style={{ fontSize: '12px' }}>{t('Gross Pay')}</Text>}
              value={formatNumber(summary.subtotal)}
              valueStyle={{ fontSize: '20px', color: '#262626' }}
              prefix="฿"
            />
          </Card>
        </Col>
      </Row>

      {/* Payment Breakdown - Simplified and Subtle */}
      <Card 
        title={<Space><DollarOutlined style={{ color: '#595959' }} /> <Text style={{ color: '#595959' }}>{t('Payment Summary')}</Text></Space>}
        size="small"
        style={{ marginBottom: 24 }}
        bordered={false}
      >
        <div style={{ padding: '12px 0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
            <Text>{t('Gross Earnings')}</Text>
            <Text strong style={{ fontSize: '15px' }}>฿ {formatNumber(summary.subtotal)}</Text>
          </div>
          
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
            <Text type="secondary">{t('Withholding Tax')} (3%)</Text>
            <Text type="danger" style={{ fontSize: '14px' }}>- ฿ {formatNumber(summary.withholdingTax)}</Text>
          </div>
          
          {payStubData.otherDeductions && (
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
              <Text type="secondary">{t('Other Deductions')}</Text>
              <Text type="danger" style={{ fontSize: '14px' }}>- ฿ {formatNumber(payStubData.otherDeductions)}</Text>
            </div>
          )}
        </div>

        {/* Net Pay - Subtle but Clear */}
        <Card 
          style={{ 
            backgroundColor: '#f6f6f6',
            border: '1px solid #e8e8e8',
            marginTop: '16px'
          }}
        >
          <Row align="middle" justify="space-between">
            <Col>
              <Text strong style={{ fontSize: '14px', color: '#595959' }}>
                {t('NET PAY')}
              </Text>
            </Col>
            <Col>
              <Title level={3} style={{ margin: 0, color: '#262626' }}>
                ฿ {formatNumber(summary.netPay)}
              </Title>
            </Col>
          </Row>
        </Card>
      </Card>

      {/* Optional: Brief note about the payment */}
      {payStubData.paymentNote && (
        <Card size="small" bordered={false} style={{ backgroundColor: '#fafafa', marginBottom: 24 }}>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            <Text strong>Note:</Text> {payStubData.paymentNote}
          </Text>
        </Card>
      )}

      {/* Download Button */}
      {!isButtonHidden && (
        <div style={{ textAlign: 'center', marginTop: 24 }}>
          <Button 
            type="primary" 
            icon={<DownloadOutlined />} 
            onClick={handleDownloadPDF}
            size="large"
            style={{ backgroundColor: '#595959', borderColor: '#595959' }}
          >
            {t('Download PDF')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default PayStubModal;